import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useCreatePosition,
  useDeletePosition,
  useTogglePositionActive,
  useDepartments,
  usePositions,
  useUpdatePosition,
  type PositionCategory,
} from '@/hooks/use-organizations';
import type { PositionWithRelations } from '@/types/employee';
import { SettingsSectionHeader } from '@/components/layout/settings-section-header';
import { cn } from '@/lib/utils';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  Briefcase,
  Check,
  ClipboardList,
  Code,
  Crown,
  DollarSign,
  FlaskConical,
  Globe,
  Headphones,
  Loader2,
  Megaphone,
  Palette,
  Pencil,
  PenTool,
  Plus,
  Rocket,
  Search,
  Server,
  Shield,
  Sparkles,
  Trash2,
  TrendingUp,
  Users,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';

const routePath = '/settings/positions' as const;
export const Route = createFileRoute(routePath)({
  beforeLoad: () => {
    throw redirect({ to: '/settings', search: { tab: 'positions' } });
  },
});

const AUTO_CATEGORY = 'auto';

const CATEGORY_LABELS: Record<PositionCategory, string> = {
  cLevel: 'C-Level',
  tech: 'Tech',
  sales: 'Sales',
  finance: 'Finance',
  projectManagers: 'Project Managers',
  hr: 'HR',
  other: 'Other',
};

const NO_DEPARTMENT = 'none';
const AUTO_ICON = 'auto';

/** Curated icon set for positions; stored by lucide component name. */
const ICON_CHOICES: Array<{ name: string; Icon: LucideIcon }> = [
  { name: 'Code', Icon: Code },
  { name: 'Server', Icon: Server },
  { name: 'Palette', Icon: Palette },
  { name: 'PenTool', Icon: PenTool },
  { name: 'FlaskConical', Icon: FlaskConical },
  { name: 'Rocket', Icon: Rocket },
  { name: 'Megaphone', Icon: Megaphone },
  { name: 'TrendingUp', Icon: TrendingUp },
  { name: 'DollarSign', Icon: DollarSign },
  { name: 'Users', Icon: Users },
  { name: 'Crown', Icon: Crown },
  { name: 'ClipboardList', Icon: ClipboardList },
  { name: 'Headphones', Icon: Headphones },
  { name: 'Shield', Icon: Shield },
  { name: 'Wrench', Icon: Wrench },
  { name: 'Globe', Icon: Globe },
  { name: 'Briefcase', Icon: Briefcase },
];
const ICONS_BY_NAME = new Map(ICON_CHOICES.map((c) => [c.name, c.Icon]));

/** Auto icon when the position has none picked. */
const CATEGORY_ICONS: Record<PositionCategory, LucideIcon> = {
  cLevel: Crown,
  tech: Code,
  sales: TrendingUp,
  finance: DollarSign,
  projectManagers: ClipboardList,
  hr: Users,
  other: Briefcase,
};

function resolvePositionIcon(position: PositionWithRelations): LucideIcon {
  if (position.icon) {
    const picked = ICONS_BY_NAME.get(position.icon);
    if (picked) return picked;
  }
  return CATEGORY_ICONS[(position.category ?? 'other') as PositionCategory];
}

type SortKey = 'title' | 'category' | 'department' | 'employees';

function sortValue(p: PositionWithRelations, key: SortKey): string | number {
  switch (key) {
    case 'title':
      return p.title ?? '';
    case 'category':
      return CATEGORY_LABELS[(p.category ?? 'other') as PositionCategory];
    case 'department':
      return p.department?.name ?? '';
    case 'employees':
      return p._count?.employees ?? 0;
  }
}

interface FormData {
  title: string;
  category: string; // AUTO_CATEGORY or a PositionCategory value
  departmentId: string; // NO_DEPARTMENT or a uuid
  description: string;
  icon: string; // AUTO_ICON or a lucide icon name from ICON_CHOICES
}

const initialFormData: FormData = {
  title: '',
  category: AUTO_CATEGORY,
  departmentId: NO_DEPARTMENT,
  description: '',
  icon: AUTO_ICON,
};

function getApiErrorMessage(err: unknown): string {
  const fallback = 'Something went wrong. Please try again.';
  if (typeof err !== 'object' || err === null) return fallback;
  const response = (err as { response?: { data?: { message?: string | string[] } } }).response;
  const message = response?.data?.message;
  if (Array.isArray(message)) return message.join('; ');
  return message ?? fallback;
}

export function PositionsPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<PositionWithRelations | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<{ title?: string; server?: string }>({});
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'title',
    dir: 'asc',
  });

  // includeInactive: Settings shows archived positions too (dimmed) so HR can
  // restore them; the assignment pickers use usePositions() (active only).
  const { data: positions = [], isLoading, error } = usePositions(true);
  const { data: departments = [] } = useDepartments();
  const createMutation = useCreatePosition();
  const updateMutation = useUpdatePosition();
  const deleteMutation = useDeletePosition();
  const toggleActiveMutation = useTogglePositionActive();

  const filteredPositions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? positions.filter((p) => {
          const haystack = [p.title, p.department?.name, CATEGORY_LABELS[(p.category ?? 'other') as PositionCategory]]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(needle);
        })
      : positions;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [positions, search, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  const validate = (): boolean => {
    const title = formData.title.trim();
    if (title.length < 2 || title.length > 150) {
      setErrors({ title: 'Title must be between 2 and 150 characters' });
      return false;
    }
    setErrors({});
    return true;
  };

  const buildPayload = (mode: 'create' | 'edit') => ({
    title: formData.title.trim(),
    description: formData.description.trim() || undefined,
    departmentId: formData.departmentId === NO_DEPARTMENT ? undefined : formData.departmentId,
    // Auto icon → store null so the card derives it from the category.
    icon: formData.icon === AUTO_ICON ? null : formData.icon,
    // create: omit on auto → backend detects from the title;
    // edit: AUTO isn't offered, so this is always a concrete value there.
    ...(formData.category !== AUTO_CATEGORY
      ? { category: formData.category as PositionCategory }
      : mode === 'edit'
        ? { category: 'other' as PositionCategory }
        : {}),
  });

  const handleCreate = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const handleEdit = (position: PositionWithRelations) => {
    setSelectedPosition(position);
    setFormData({
      title: position.title,
      category: position.category ?? 'other',
      departmentId: position.department?.id ?? NO_DEPARTMENT,
      description: position.description ?? '',
      icon: position.icon && ICONS_BY_NAME.has(position.icon) ? position.icon : AUTO_ICON,
    });
    setErrors({});
    setEditDialogOpen(true);
  };

  const handleDelete = (position: PositionWithRelations) => {
    setSelectedPosition(position);
    setErrors({});
    setDeleteDialogOpen(true);
  };

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    createMutation.mutate(buildPayload('create'), {
      onSuccess: () => {
        setCreateDialogOpen(false);
        resetForm();
      },
      onError: (err) => setErrors((prev) => ({ ...prev, server: getApiErrorMessage(err) })),
    });
  };

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPosition || !validate()) return;
    updateMutation.mutate(
      { id: selectedPosition.id, ...buildPayload('edit') },
      {
        onSuccess: () => {
          setEditDialogOpen(false);
          resetForm();
        },
        onError: (err) => setErrors((prev) => ({ ...prev, server: getApiErrorMessage(err) })),
      }
    );
  };

  const confirmDelete = () => {
    if (!selectedPosition) return;
    deleteMutation.mutate(selectedPosition.id, {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        setSelectedPosition(null);
      },
      onError: (err) => setErrors({ server: getApiErrorMessage(err) }),
    });
  };

  const closeOnIdle = (setter: (open: boolean) => void) => (open: boolean) => {
    if (!open) {
      setter(false);
      resetForm();
    }
  };

  const categoryField = (idPrefix: string) => (
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-category`}>Category</Label>
      <Select
        value={formData.category}
        onValueChange={(v) => setFormData((f) => ({ ...f, category: v }))}
      >
        <SelectTrigger id={`${idPrefix}-category`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {idPrefix === 'create' && (
            <SelectItem value={AUTO_CATEGORY}>Auto-detect from title</SelectItem>
          )}
          {(Object.keys(CATEGORY_LABELS) as PositionCategory[]).map((c) => (
            <SelectItem key={c} value={c}>
              {CATEGORY_LABELS[c]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const departmentField = (idPrefix: string) => (
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-department`}>Department</Label>
      <Select
        value={formData.departmentId}
        onValueChange={(v) => setFormData((f) => ({ ...f, departmentId: v }))}
      >
        <SelectTrigger id={`${idPrefix}-department`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_DEPARTMENT}>No department</SelectItem>
          {departments.map((d) => (
            <SelectItem key={d.id} value={d.id}>
              {d.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const formFields = (idPrefix: string, pending: boolean) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-title`}>
          Title <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${idPrefix}-title`}
          value={formData.title}
          onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Senior Software Engineer"
          disabled={pending}
          maxLength={150}
        />
        {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
      </div>
      {categoryField(idPrefix)}
      <div className="space-y-2">
        <Label>Icon</Label>
        <div className="grid grid-cols-9 gap-1.5">
          <Button
            type="button"
            variant={formData.icon === AUTO_ICON ? 'secondary' : 'outline'}
            size="icon"
            disabled={pending}
            onClick={() => setFormData((f) => ({ ...f, icon: AUTO_ICON }))}
            title="Auto — icon from category"
            aria-label="Auto icon from category"
            className={cn(
              // A selected icon tile, not a primary action: `secondary`, not
              // the mint [HW-926]
              'h-9 w-full cursor-pointer',
              formData.icon === AUTO_ICON && 'ring-2 ring-hygge ring-offset-1'
            )}
          >
            <Sparkles className="h-4 w-4" />
          </Button>
          {ICON_CHOICES.map(({ name, Icon }) => (
            <Button
              key={name}
              type="button"
              variant={formData.icon === name ? 'secondary' : 'outline'}
              size="icon"
              disabled={pending}
              onClick={() => setFormData((f) => ({ ...f, icon: name }))}
              title={name}
              aria-label={`Icon ${name}`}
              className={cn(
                // A selected icon tile, not a primary action: `secondary`, not
                // the mint [HW-926]
                'h-9 w-full cursor-pointer',
                formData.icon === name && 'ring-2 ring-hygge ring-offset-1'
              )}
            >
              <Icon className="h-4 w-4" />
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Auto picks an icon from the category
          {formData.category !== AUTO_CATEGORY &&
            ` (${CATEGORY_LABELS[formData.category as PositionCategory]})`}
          .
        </p>
      </div>
      {departmentField(idPrefix)}
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-description`}>Description</Label>
        <Textarea
          id={`${idPrefix}-description`}
          value={formData.description}
          onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
          placeholder="Optional — responsibilities, scope…"
          disabled={pending}
          maxLength={500}
          rows={3}
        />
      </div>
      {errors.server && (
        <p className="flex items-center gap-1.5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {errors.server}
        </p>
      )}
    </div>
  );

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-4xl space-y-4 p-6">
        <SettingsSectionHeader
          title="Positions"
          description="Manage job position titles, categories and departments"
          actions={
            <>
              <div className="relative w-full max-w-xs">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search positions…"
                  className="h-9 pl-8"
                />
              </div>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      className="h-9 w-9 shrink-0 cursor-pointer rounded-full"
                      onClick={handleCreate}
                      aria-label="Add position"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Add position</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          }
        />

        {/* List */}
        {error ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="mt-3 text-sm text-muted-foreground">
                Failed to load positions. Refresh the page to try again.
              </p>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <div className="overflow-hidden rounded-xl border">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex animate-pulse items-center gap-3 border-b px-4 py-3 last:border-0"
              >
                <div className="h-8 w-8 rounded-full bg-muted" />
                <div className="h-4 w-56 rounded bg-muted" />
                <div className="ml-auto h-4 w-16 rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : filteredPositions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Briefcase className="h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                {search.trim()
                  ? 'No positions match your search.'
                  : 'No positions yet. Create the first one.'}
              </p>
              {!search.trim() && (
                <Button className="mt-4 cursor-pointer gap-2" onClick={handleCreate}>
                  <Plus className="h-4 w-4" />
                  New position
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-hidden rounded-xl border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                  {(
                    [
                      { key: 'title', label: 'Position', align: 'left' },
                      { key: 'category', label: 'Category', align: 'left' },
                      { key: 'department', label: 'Department', align: 'left' },
                      { key: 'employees', label: 'People', align: 'right' },
                    ] as Array<{ key: SortKey; label: string; align: 'left' | 'right' }>
                  ).map((col) => (
                    <th
                      key={col.key}
                      className={cn(
                        'cursor-pointer select-none px-4 py-2.5 font-medium hover:text-foreground',
                        col.align === 'right' && 'text-right'
                      )}
                      onClick={() => toggleSort(col.key)}
                    >
                      <span
                        className={cn(
                          'inline-flex items-center gap-1',
                          col.align === 'right' && 'flex-row-reverse'
                        )}
                      >
                        {col.label}
                        {sort.key === col.key &&
                          (sort.dir === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                  ))}
                  <th className="w-[128px] px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {filteredPositions.map((position) => {
                  const employeeCount = position._count?.employees ?? 0;
                  const inUse = employeeCount > 0;
                  const PositionIcon = resolvePositionIcon(position);
                  return (
                    <tr
                      key={position.id}
                      className={cn(
                        'group border-b last:border-0 hover:bg-muted/30',
                        !position.isActive && 'opacity-60'
                      )}
                    >
                      <td className="max-w-0 px-4 py-2.5">
                        <span className="flex items-center gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-hygge/10 text-hygge">
                            <PositionIcon className="h-[18px] w-[18px]" />
                          </span>
                          <span className="min-w-0">
                            <span className="flex items-center gap-2">
                              <span className="truncate font-medium">{position.title}</span>
                              {!position.isActive && (
                                <span className="shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                                  Archived
                                </span>
                              )}
                            </span>
                            {position.description && (
                              <span className="block truncate text-xs text-muted-foreground">
                                {position.description}
                              </span>
                            )}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="whitespace-nowrap rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                          {CATEGORY_LABELS[(position.category ?? 'other') as PositionCategory]}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {position.department?.name ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{employeeCount}</td>
                      <td className="px-4 py-2.5">
                        <span className="flex items-center justify-end gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 cursor-pointer rounded-full hover:bg-muted"
                                  onClick={() => handleEdit(position)}
                                  aria-label={`Edit ${position.title}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit position</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 cursor-pointer rounded-full hover:bg-muted"
                                  onClick={() => toggleActiveMutation.mutate(position.id)}
                                  disabled={toggleActiveMutation.isPending}
                                  aria-label={
                                    position.isActive
                                      ? `Archive ${position.title}`
                                      : `Restore ${position.title}`
                                  }
                                >
                                  {position.isActive ? (
                                    <Archive className="h-4 w-4" />
                                  ) : (
                                    <ArchiveRestore className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {position.isActive ? 'Archive position' : 'Restore position'}
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 cursor-pointer rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() => handleDelete(position)}
                                    disabled={inUse}
                                    aria-label={`Delete ${position.title}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {inUse
                                  ? `${employeeCount} employee(s) hold this position — reassign them first`
                                  : 'Delete position'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createDialogOpen} onOpenChange={closeOnIdle(setCreateDialogOpen)}>
        <DialogContent>
          <form onSubmit={submitCreate}>
            <DialogHeader>
              <DialogTitle>New position</DialogTitle>
              <DialogDescription>
                Add a job position. Category is detected from the title unless you pick one.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">{formFields('create', createMutation.isPending)}</div>
            <DialogFooter>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0 cursor-pointer rounded-full"
                      onClick={() => closeOnIdle(setCreateDialogOpen)(false)}
                      disabled={createMutation.isPending}
                      aria-label="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Cancel</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="submit"
                      size="icon"
                      className="h-9 w-9 shrink-0 cursor-pointer rounded-full"
                      disabled={createMutation.isPending}
                      aria-label="Create position"
                    >
                      {createMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Create position</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onOpenChange={closeOnIdle(setEditDialogOpen)}>
        <DialogContent>
          <form onSubmit={submitEdit}>
            <DialogHeader>
              <DialogTitle>Edit position</DialogTitle>
              <DialogDescription>Update the position details.</DialogDescription>
            </DialogHeader>
            <div className="py-4">{formFields('edit', updateMutation.isPending)}</div>
            <DialogFooter>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0 cursor-pointer rounded-full"
                      onClick={() => closeOnIdle(setEditDialogOpen)(false)}
                      disabled={updateMutation.isPending}
                      aria-label="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Cancel</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="submit"
                      size="icon"
                      className="h-9 w-9 shrink-0 cursor-pointer rounded-full"
                      disabled={updateMutation.isPending}
                      aria-label="Save changes"
                    >
                      {updateMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Save changes</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{selectedPosition?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the position from the dictionary. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {errors.server && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {errors.server}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleteMutation.isPending}
              className="h-9 w-9 shrink-0 rounded-full p-0"
              aria-label="Cancel"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleteMutation.isPending}
              className="h-9 w-9 shrink-0 rounded-full p-0 bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label="Delete position"
              title="Delete position"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
